<template>
  <div class="main w-50 ml-auto mr-auto">
    <div class="card no-shadow m-0">
      <div class="card-header card-header-primary negative blue-grad w-50 d-flex flex-column">
        <h4 class="card-title">{{ $t("message.company_history_sales.company_historical_sales") }}</h4>
      </div>

      <div class="card-body mt-3">
        <b-tabs pills content-class="mt-3" small v-model="current_tab">
          <b-tab :title="$t('message.company_history_sales.search_button')" active>
            <div class="mb-3">
              <label>{{ $t("message.company_history_sales.select_month") }}</label>
              <div class="d-flex" style="gap: 10px;">
                <select
                  class="form-control"
                  style="height: 50px; width: 120px;"
                  v-model="selectedYear"
                  :disabled="isSearching"
                >
                  <option value="" disabled>{{ $t("message.company_history_sales.year") || 'Year' }}</option>
                  <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                </select>
                <select
                  class="form-control"
                  style="height: 50px; width: 120px;"
                  v-model="selectedMonth"
                  :disabled="isSearching"
                >
                  <option value="" disabled>{{ $t("message.company_history_sales.month") || 'Month' }}</option>
                  <option v-for="m in monthOptions" :key="m" :value="m">{{ m }}</option>
                </select>
              </div>
            </div>

            <div class="input-group mb-3">
              <input
                type="text"
                class="form-control"
                style="height: 50px"
                v-model="searchKeyword"
                :disabled="isSearching"
                :placeholder="$t('message.company_history_sales.search_keyword_placeholder')"
              />

              <div class="input-group-append">
                <button
                  style="height: 50px"
                  class="btn btn-primary"
                  @click="search"
                  :disabled="isSearching"
                >
                  <span v-if="isSearching" class="spinner-border spinner-border-sm"></span>
                  {{ isSearching ? $t("message.company_history_sales.searching") : $t("message.company_history_sales.search_button") }}
                </button>
              </div>
            </div>

            <div v-if="isSearching" class="text-center py-4">
              <b-progress :value="loadingProgress" :max="100" show-progress animated></b-progress>
              <div class="mt-2">{{ loadingMessage }}</div>
            </div>

            <div v-if="isSearched && !isSearching && results.length === 0" class="alert alert-info">
              {{ $t("message.company_history_sales.no_results_found") }}
            </div>

            <div v-if="results.length > 0 && !isSearching">

              <div class="mb-4">
                <h5>{{ $t("message.company_history_sales.chart_title") }}</h5>
                <div style="position: relative; height: 350px;">
                  <canvas ref="salesChart"></canvas>
                </div>
              </div>

              <table class="table table-bordered table-striped">
                <thead class="thead-light">
                  <tr>
                    <th>{{ $t("message.company_history_sales.month") }}</th>
                    <th>{{ $t("message.company_history_sales.credit_code") }}</th>
                    <th>{{ $t("message.company_history_sales.company_name") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_volume") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_amount") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_volume_yoy") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_amount_yoy") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_volume_sum") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_amount_sum") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_volume_sum_yoy") }}</th>
                    <th>{{ $t("message.company_history_sales.sales_amount_sum_yoy") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in results" :key="index">
                    <td>{{ item.DataMonth }}</td>
                    <td>{{ item.CreditCode }}</td>
                    <td>{{ item.CompanyNameTranslated || item.CompanyName }}</td>
                    <td>{{ item.CompanySalesVolume }}</td>
                    <td>{{ item.CompanySalesAmount }}</td>
                    <td>{{ item.CompanySalesVolumeYoy ? item.CompanySalesVolumeYoy + '%' : '' }}</td>
                    <td>{{ item.CompanySalesAmountYoy ? item.CompanySalesAmountYoy + '%' : '' }}</td>
                    <td>{{ item.CompanySalesVolumeSum }}</td>
                    <td>{{ item.CompanySalesAmountSum }}</td>
                    <td>{{ item.CompanySalesVolumeSumYoy ? item.CompanySalesVolumeSumYoy + '%' : '' }}</td>
                    <td>{{ item.CompanySalesAmountSumYoy ? item.CompanySalesAmountSumYoy + '%' : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-tab>
          
          <!-- <b-tab :title="$t('message.company_history_sales.history')" lazy>
            <CNSearchHistory />
          </b-tab> -->
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import CNSearchHistory from "../helpers/history.vue";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, LineController, LineElement, PointElement, CategoryScale, LinearScale);

const YEAR_COLORS = [
  "#4dc9f6", "#f67019", "#f53794", "#537bc4",
  "#acc236", "#166a8f", "#00a950", "#58595b"
];

export default {
  components: { CNSearchHistory },

  data() {
    return {
      searchKeyword: "",
      selectedYear: "",
      selectedMonth: "",
      isSearching: false,
      isSearched: false,
      results: [],
      resultsOriginal: [],
      current_tab: 0,
      loadingProgress: 0,
      loadingMessage: "",
      prices: {},
      currency: "",
      user_balance: 0,
      history_record_id: null,
      reduceBalanceId: null,
      SERVICE_CODE: "1105",
      chartInstance: null
    };
  },

  computed: {
    dataMonth() {
      if (this.selectedYear && this.selectedMonth) {
        return `${this.selectedYear}-${this.selectedMonth}`;
      }
      return "";
    },
    yearOptions() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current; y >= current - 40; y--) {
        years.push(String(y));
      }
      return years;
    },
    monthOptions() {
      return ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    }
  },

  watch: {
    results: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      }
    }
  },

  mounted() {
    this.loadPrices();
    if (!this.$store.state.auth) {
      this.$router.push({ name: "sign-in" });
    }
  },

  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },

  methods: {
    renderChart() {
      const canvas = this.$refs.salesChart;
      if (!canvas || this.results.length === 0) return;

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const monthLabels = [
        "01", "02", "03", "04", "05", "06",
        "07", "08", "09", "10", "11", "12"
      ];

      const byYear = {};
      for (const item of this.results) {
        if (!item.DataMonth) continue;
        const [year, month] = item.DataMonth.split("-");
        if (!byYear[year]) byYear[year] = {};
        byYear[year][month] = parseFloat(item.CompanySalesAmountSum) || 0;
      }

      const years = Object.keys(byYear).sort();
      const datasets = years.map((year, idx) => ({
        label: year,
        data: monthLabels.map(m => byYear[year][m] ?? null),
        borderColor: YEAR_COLORS[idx % YEAR_COLORS.length],
        backgroundColor: YEAR_COLORS[idx % YEAR_COLORS.length],
        tension: 0.3,
        spanGaps: true,
        pointRadius: 4
      }));

      this.chartInstance = new ChartJS(canvas, {
        type: "line",
        data: {
          labels: monthLabels,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y} (10k RMB)`
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: this.$t("message.company_history_sales.month")
              }
            },
            y: {
              title: {
                display: true,
                text: this.$t("message.company_history_sales.sales_amount_sum")
              }
            }
          }
        }
      });
    },

    loadPrices() {
      axios
        .get(window.base + "/account/GetServicesPrice")
        .then((response) => {
          if (typeof response.data === "object") {
            const r = response.data.find(
              (element) => element.billing_service_code === this.SERVICE_CODE
            );
            if (r) {
              this.currency = r.billing_service_cur;
              this.prices[this.SERVICE_CODE] = this.$store.state.user.is_privileged
                ? r.service_price_privileged
                : r.billing_service_price;
            }
          }
        })
        .catch(() => {
          console.log("Error loading pricelist...");
        });
    },

    async search() {
      if (!this.searchKeyword || !this.dataMonth) {
        this.$bvModal.msgBoxOk(this.$t("message.company_history_sales.fill_all_fields"), {
          title: this.$t("message.confirm"),
          size: "md",
          buttonSize: "sm",
          okVariant: "success",
          centered: true
        });
        return;
      }

      const balanceResponse = await axios.get(window.base + "/account/balance");
      this.user_balance = parseFloat(balanceResponse.data) || 0;
      const price = this.prices[this.SERVICE_CODE] || 0;

      if (this.user_balance < price) {
        this.$bvModal.msgBoxOk(this.$t("message.account.not_enough_funds_message"), {
          title: this.$t("message.confirm"),
          size: "md",
          buttonSize: "sm",
          okVariant: "success",
          centered: true
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("message.company_history_sales.cn_single_service_alert")
          .replace("{}", this.$t("message.company_history_sales.company_historical_sales")) +
          " " + window.rubFormat(price, this.currency),
        {
          title: this.$t("message.confirm"),
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.$t("message.yes"),
          cancelTitle: this.$t("message.no"),
          centered: true
        }
      );

      if (!confirmed) return;

      this.isSearching = true;
      this.isSearched = false;
      this.results = [];
      this.loadingProgress = 10;
      this.loadingMessage = this.$t("message.company_history_sales.services_loading_data");

      try {
        const fd = new FormData();
        fd.append("amount", price);
        fd.append("service_code", this.SERVICE_CODE);
        fd.append("search_keyword", this.searchKeyword);
        const reduceBalanceData = (await axios.post(window.base + "/account/reduceBalance", fd)).data;
        this.reduceBalanceId = reduceBalanceData?.id || null;
        this.$root.$emit("reduceBalance", { price: price });

        this.loadingProgress = 30;

        const response = await axios.get(`${window.base}/library/GetQccAPIData`, {
          params: {
            serviceCode: this.SERVICE_CODE,
            keyWord: this.searchKeyword,
            dataMonth: this.dataMonth
          }
        });

        this.loadingProgress = 60;
        this.loadingMessage = this.$t("message.company_history_sales.services_loading_translations");

        const parsed = typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;

        this.resultsOriginal = parsed?.Result?.Data?.CompanyMonthSaleInfo || [];

        if (this.resultsOriginal.length > 0) {
          this.results = await this.translateResults(this.resultsOriginal);
        }

        this.loadingProgress = 80;

        const hasResults = this.results.length > 0;
        if (this.reduceBalanceId) {
          const updateFd = new FormData();
          updateFd.append("id", this.reduceBalanceId);
          updateFd.append("hasResults", hasResults);
          await axios.post(window.base + "/account/updateOperationResult", updateFd);
        }

        await this.saveHistory(parsed);

        this.loadingProgress = 100;
      } catch (error) {
        console.error("API Error:", error);
        this.$bvModal.msgBoxOk(
          this.$t("message.company_history_sales.error_loading_service").replace("{}", this.SERVICE_CODE),
          {
            title: this.$t("message.error"),
            size: "md",
            buttonSize: "sm",
            okVariant: "danger",
            centered: true
          }
        );
      }

      this.isSearching = false;
      this.isSearched = true;
      this.$nextTick(() => this.renderChart());
    },

    async translateResults(results) {
      const locale = this.getTargetLocale();
      if (locale === "zh") {
        return results;
      }

      try {
        const companyNames = results.map((item) => item.CompanyName || "");

        const response = await axios.post(
          `${window.base}/library/TranslateNextArray?source=zh&target=${locale}`,
          companyNames
        );

        return results.map((item, index) => {
          let translatedName = item.CompanyName;
          try {
            translatedName = JSON.parse(response.data[index]).translation;
          } catch (e) {
            console.warn("Translation parse error", e);
          }
          return {
            ...item,
            CompanyNameTranslated: translatedName
          };
        });
      } catch (error) {
        console.error("Translation error:", error);
        return results;
      }
    },

    getTargetLocale() {
      const locale = this.$i18n.locale;
      switch (locale) {
        case "cn":
          return "zh";
        case "en":
          return "en";
        case "ru":
          return "ru";
        default:
          return "en";
      }
    },

    async saveHistory(responseData) {
      const requestlog = {
        service_code: this.SERVICE_CODE,
        search_text: `${this.searchKeyword} | ${this.dataMonth}`,
        result: JSON.stringify({
          [this.SERVICE_CODE]: {
            service_code: this.SERVICE_CODE,
            search_text: `${this.searchKeyword} | ${this.dataMonth}`,
            result: this.results
          }
        }),
        china_result: JSON.stringify(responseData)
      };

      try {
        const response = await axios.post(
          window.base + "/contract/saveContrHistory",
          requestlog
        );
        this.history_record_id = response.data;
      } catch (error) {
        console.error("Error saving history:", error);
      }
    },
  }
};
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
}
</style>
